// Command Palette (⌘K / Ctrl+K) for the FlowMind app
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, LayoutDashboard, Workflow, Plus, BarChart3, Plug, LogOut, Sparkles, ArrowRight,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { logout } = useAuth();

  const close = useCallback(() => { setOpen(false); setQ(""); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const commands = [
    { id: "dashboard", label: "Aller au tableau de bord", icon: LayoutDashboard, action: () => navigate("/app/dashboard"), kind: "Navigation" },
    { id: "workflows", label: "Voir mes workflows", icon: Workflow, action: () => navigate("/app/workflows"), kind: "Navigation" },
    { id: "new-workflow", label: "Créer un nouveau workflow", icon: Plus, action: () => navigate("/app/workflows/new"), kind: "Action", shortcut: "N" },
    { id: "ai-generate", label: "Générer un workflow avec l'IA", icon: Sparkles, action: () => navigate("/app/workflows/new"), kind: "Action" },
    { id: "analytics", label: "Voir les analytics & ROI", icon: BarChart3, action: () => navigate("/app/analytics"), kind: "Navigation" },
    { id: "integrations", label: "Gérer les intégrations", icon: Plug, action: () => navigate("/app/integrations"), kind: "Navigation" },
    { id: "logout", label: "Se déconnecter", icon: LogOut, action: () => { logout(); navigate("/"); }, kind: "Compte" },
  ];

  const filtered = commands.filter((c) => c.label.toLowerCase().includes(q.toLowerCase()));

  const run = (cmd) => { close(); setTimeout(() => cmd.action(), 50); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-start justify-center pt-[15vh] px-4"
          onClick={close}
          data-testid="command-palette"
        >
          <motion.div
            initial={{ scale: 0.96, y: -10, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: -10, opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-xl bg-white rounded-sm border border-zinc-200 shadow-2xl overflow-hidden"
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-200">
              <Search size={16} className="text-zinc-400"/>
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Tapez une commande ou recherchez..."
                className="flex-1 outline-none text-sm bg-transparent placeholder:text-zinc-400"
                data-testid="command-palette-input"
              />
              <span className="text-[10px] font-mono text-zinc-400 border border-zinc-200 rounded-sm px-1.5 py-0.5">ESC</span>
            </div>
            <div className="max-h-96 overflow-y-auto py-2">
              {filtered.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-zinc-500">Aucun résultat pour « {q} »</p>
              ) : (
                filtered.map((cmd) => {
                  const Icon = cmd.icon;
                  return (
                    <button
                      key={cmd.id}
                      onClick={() => run(cmd)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-zinc-100 transition-colors text-left"
                      data-testid={`cmd-${cmd.id}`}
                    >
                      <Icon size={16} strokeWidth={1.5} className="text-zinc-500 shrink-0"/>
                      <span className="text-sm flex-1">{cmd.label}</span>
                      <span className="text-[10px] font-mono text-zinc-400 uppercase">{cmd.kind}</span>
                      <ArrowRight size={12} className="text-zinc-300"/>
                    </button>
                  );
                })
              )}
            </div>
            <div className="px-4 py-2 border-t border-zinc-200 bg-zinc-50 flex items-center justify-between text-[11px] text-zinc-500">
              <span>FlowMind Command</span>
              <span className="font-mono">⌘K pour ouvrir / fermer</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
